/**
 * Encryption Status Badge - Shows current encryption key status
 *
 * Displays in the header to indicate:
 * - Whether user has an active key
 * - Key owner type/role
 * - Connection to key management
 */

import { Box, Chip, Tooltip, Typography, Menu, MenuItem, Divider } from '@mui/material';
import { useState } from 'react';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import { useCryptoKeyStore, useKeyBackup, KeyOwnerType } from '@antragsmanager/crypto-lib';

interface EncryptionStatusBadgeProps {
  /** Show detailed menu on click */
  showMenu?: boolean;

  /** Callback for logout */
  onLogout?: () => void;
}

export function EncryptionStatusBadge({
  showMenu = true,
  onLogout,
}: EncryptionStatusBadgeProps) {
  const activeKey = useCryptoKeyStore((state) => state.activeKey);
  const { downloadPdf, isGenerating } = useKeyBackup();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (showMenu && activeKey) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadBackup = async () => {
    if (activeKey) {
      try {
        await downloadPdf({
          ownerName: activeKey.ownerName ?? 'Unbekannt',
          ownerType: activeKey.ownerType,
        });
      } catch (error) {
        console.error('Failed to download backup:', error);
      }
    }
    handleClose();
  };

  const handleLogout = () => {
    handleClose();
    onLogout?.();
  };

  // Get role display name
  const getRoleDisplayName = (ownerType: KeyOwnerType): string => {
    switch (ownerType) {
      case 'CITIZEN':
        return 'Bürger';
      case 'AUTHORITY':
        return 'Behörde';
      case 'ADMIN':
        return 'Administrator';
      default:
        return ownerType;
    }
  };

  // Get role color
  const getRoleColor = (
    ownerType: KeyOwnerType
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (ownerType) {
      case 'ADMIN':
        return 'secondary';
      case 'AUTHORITY':
        return 'primary';
      case 'CITIZEN':
        return 'default';
      default:
        return 'default';
    }
  };

  // Format fingerprint short
  const formatFingerprintShort = (fingerprint: string): string => {
    return fingerprint.slice(0, 8).toUpperCase();
  };

  if (!activeKey) {
    return (
      <Tooltip title="Kein Schlüssel aktiv">
        <Chip
          icon={<WarningIcon />}
          label="Nicht angemeldet"
          size="small"
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip
        title={
          <Box>
            <Typography variant="body2">
              {activeKey.ownerName ?? 'Verschlüsselung aktiv'}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {formatFingerprintShort(activeKey.fingerprint)}
            </Typography>
          </Box>
        }
      >
        <Chip
          icon={<VerifiedUserIcon />}
          label={
            activeKey.ownerType
              ? getRoleDisplayName(activeKey.ownerType)
              : 'Verschlüsselt'
          }
          size="small"
          color={activeKey.ownerType ? getRoleColor(activeKey.ownerType) : 'success'}
          onClick={handleClick}
          sx={{ cursor: showMenu ? 'pointer' : 'default' }}
        />
      </Tooltip>

      {showMenu && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
            <Typography variant="subtitle2">
              {activeKey.ownerName ?? 'Angemeldeter Nutzer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeKey.ownerType && getRoleDisplayName(activeKey.ownerType)}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Schlüssel-ID:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', display: 'block' }}
            >
              {activeKey.keyId.slice(0, 18)}...
            </Typography>
          </Box>

          <Divider />

          <MenuItem onClick={handleDownloadBackup} disabled={isGenerating}>
            <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
            Backup herunterladen
          </MenuItem>

          {onLogout && (
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
              Abmelden
            </MenuItem>
          )}
        </Menu>
      )}
    </>
  );
}
